/**
 * Member Service
 * Comprehensive service for all member management operations
 */

import { apiRequest } from '../utils/apiResponseHandler';
import { getApiUrl } from '../config/api';

const memberService = {

  // ==================== MEMBER MANAGEMENT ====================

  // List Members
  async getMembers(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const url = queryParams.toString()
      ? `${getApiUrl('MEMBERS')}?${queryParams.toString()}`
      : getApiUrl('MEMBERS');

    // No client-side timeout: large list may take a long time to transfer
    return await apiRequest(url, {
      method: 'GET',
      timeout: 0,
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Member by ID
  async getMemberById(memberId) {
    return await apiRequest(getApiUrl('MEMBER_BY_ID', memberId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Create Member
  async createMember(memberData) {
    return await apiRequest(getApiUrl('MEMBERS'), {
      method: 'POST',
      body: memberData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Update Member
  async updateMember(memberId, memberData) {
    return await apiRequest(getApiUrl('MEMBER_BY_ID', memberId), {
      method: 'PATCH',
      body: memberData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Delete Member
  async deleteMember(memberId) {
    return await apiRequest(getApiUrl('MEMBER_BY_ID', memberId), {
      method: 'DELETE',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },


  // Update Email Preferences
  async updateEmailPreferences(memberId, preferences) {
    return await apiRequest(getApiUrl('MEMBER_EMAIL_PREFERENCES', memberId), {
      method: 'PATCH',
      body: preferences,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // ==================== ADVANCED SEARCH ====================

  // Advanced Member Search
  async searchMembers(searchParams = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(searchParams).forEach(key => {
      if (searchParams[key] !== undefined && searchParams[key] !== '') {
        queryParams.append(key, searchParams[key]);
      }
    });

    const url = queryParams.toString()
      ? `${getApiUrl('MEMBER_SEARCH')}?${queryParams.toString()}`
      : getApiUrl('MEMBER_SEARCH');

    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Enhanced Member Search - searches across multiple fields
  async enhancedMemberSearch(searchTerm, additionalFilters = {}) {
    const queryParams = new URLSearchParams();

    // Add the main search term
    if (searchTerm && searchTerm.trim() !== '') {
      queryParams.append('search', searchTerm.trim());
    }

    // Add additional filters
    Object.keys(additionalFilters).forEach(key => {
      if (additionalFilters[key] !== undefined && additionalFilters[key] !== '') {
        queryParams.append(key, additionalFilters[key]);
      }
    });

    const url = queryParams.toString()
      ? `${getApiUrl('MEMBERS')}?${queryParams.toString()}`
      : getApiUrl('MEMBERS');

    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Smart Member Search - tries multiple search strategies
  async smartMemberSearch(searchTerm, additionalFilters = {}) {
    if (!searchTerm || searchTerm.trim() === '') {
      return await this.getMembers(additionalFilters);
    }

    const searchValue = searchTerm.trim();
    const allResults = [];
    const uniqueResults = new Set();

    try {
      // Strategy 1: Search by email (if it looks like an email) - prioritize email searches
      if (searchValue.includes('@')) {
        // Use the advanced search endpoint with email search type for precise email matching
        const emailSearchParams = {
          ...additionalFilters,
          search_term: searchValue,
          search_type: 'email'
        };
        const emailSearch = await this.searchMembers(emailSearchParams);
        if (emailSearch.success && emailSearch.data) {
          emailSearch.data.forEach(member => {
            if (!uniqueResults.has(member.id)) {
              uniqueResults.add(member.id);
              allResults.push(member);
            }
          });
        }
        
        // Return early for email searches - don't do general search which is too broad
        // This ensures email searches only return members with matching emails
        return {
          success: true,
          data: allResults,
          message: allResults.length > 0 
            ? `Found ${allResults.length} member(s) with email "${searchValue}"`
            : `No members found with email "${searchValue}"`
        };
      }

      // Strategy 2: Search by member ID (exact match)
      if (searchValue.match(/^[A-Z]{3}\d{4,}$/)) { // Pattern like ASF2025283
        const memberIdSearch = await this.getMembers({
          ...additionalFilters,
          member_id: searchValue
        });
        if (memberIdSearch.success && memberIdSearch.data) {
          memberIdSearch.data.forEach(member => {
            if (!uniqueResults.has(member.id || member.member_id)) {
              uniqueResults.add(member.id || member.member_id);
              allResults.push(member);
            }
          });
        }
        
        // Return early if member ID search found exact match
        if (allResults.length > 0) {
          return {
            success: true,
            data: allResults,
            message: `Found ${allResults.length} member(s) with ID "${searchValue}"`
          };
        }
      }

      // Strategy 3: General search (only if not an email or member ID)
      const generalSearch = await this.enhancedMemberSearch(searchValue, additionalFilters);
      if (generalSearch.success && generalSearch.data) {
        generalSearch.data.forEach(member => {
          if (!uniqueResults.has(member.id || member.member_id)) {
            uniqueResults.add(member.id || member.member_id);
            allResults.push(member);
          }
        });
      }

      // Strategy 4: Search by state (if it's a 2-3 letter code)
      if (searchValue.length <= 3 && /^[A-Z]+$/.test(searchValue)) {
        const stateSearch = await this.getMembers({
          ...additionalFilters,
          state: searchValue
        });
        if (stateSearch.success && stateSearch.data) {
          stateSearch.data.forEach(member => {
            if (!uniqueResults.has(member.member_id)) {
              uniqueResults.add(member.member_id);
              allResults.push(member);
            }
          });
        }
      }

      return {
        success: true,
        data: allResults,
        message: `Found ${allResults.length} members matching "${searchValue}"`
      };

    } catch (error) {
      // Smart search error
      // Fallback to general search
      return await this.enhancedMemberSearch(searchValue, additionalFilters);
    }
  },



  // ==================== BULK OPERATIONS ====================

  // Bulk Member Actions
  async bulkMemberAction(memberIds, action) {
    return await apiRequest(getApiUrl('MEMBER_BULK_ACTION'), {
      method: 'POST',
      body: {
        member_ids: memberIds,
        action: action
      },
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // ==================== EXTERNAL MEMBER VIEW ====================

  // Get External Member View (Public API)
  async getExternalMemberView(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const url = queryParams.toString()
      ? `${getApiUrl('EXTERNAL_VIEW')}?${queryParams.toString()}`
      : getApiUrl('EXTERNAL_VIEW');

    // For public API, we'll make a direct fetch call without authentication
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        success: true,
        data: data,
        message: 'Members loaded successfully'
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.message || 'Failed to load members'
      };
    }
  },

  // ==================== STATISTICS & ANALYTICS ====================

  // Get Member Statistics
  async getMemberStats() {
    return await apiRequest(getApiUrl('MEMBER_STATS'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Enhanced Member Statistics (with comprehensive breakdown)
  async getEnhancedMemberStats() {
    return await apiRequest(getApiUrl('MEMBER_STATS'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },


  // Get Sync Status
  async getSyncStatus() {
    return await apiRequest(getApiUrl('MEMBER_SYNC_STATUS'), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },




  // ==================== UTILITY FUNCTIONS ====================

  // Validate Member Data
  validateMemberData(memberData) {
    const errors = {};

    // Required fields
    if (!memberData.organization_name?.trim()) {
      errors.organization_name = 'Organization name is required';
    }

    if (!memberData.primary_contact_name?.trim()) {
      errors.primary_contact_name = 'Primary contact name is required';
    }

    if (!memberData.primary_contact_email?.trim()) {
      errors.primary_contact_email = 'Primary contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(memberData.primary_contact_email)) {
      errors.primary_contact_email = 'Invalid email format';
    }

    if (!memberData.phone_number?.trim()) {
      errors.phone_number = 'Phone number is required';
    }

    if (!memberData.city?.trim()) {
      errors.city = 'City is required';
    }

    if (!memberData.state?.trim()) {
      errors.state = 'State is required';
    }

    // Optional field validation
    if (memberData.website && !/^https?:\/\/.+/.test(memberData.website)) {
      errors.website = 'Website must be a valid URL';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Format Member Data for API
  formatMemberDataForAPI(memberData) {
    return {
      organization_name: memberData.organization_name?.trim(),
      primary_contact_name: memberData.primary_contact_name?.trim(),
      primary_contact_email: memberData.primary_contact_email?.trim(),
      phone_number: memberData.phone_number?.trim(),
      website: memberData.website?.trim() || '',
      address: memberData.address?.trim() || '',
      city: memberData.city?.trim(),
      state: memberData.state?.trim(),
      country: memberData.country?.trim() || 'Australia',
      postal_code: memberData.postal_code?.trim() || '',
      description: memberData.description?.trim() || '',
      screening_format: memberData.screening_format || 'dvd',
      publication_preferences: memberData.publication_preferences || 'newsletter',
      membership_status: memberData.membership_status || 'active',
      is_active: memberData.is_active !== false,
      email_subscribed: memberData.email_subscribed !== false,
      show_in_external_view: memberData.show_in_external_view !== false,
    };
  },

  // Format Search Parameters
  formatSearchParams(searchParams) {
    const formatted = {};

    Object.keys(searchParams).forEach(key => {
      const value = searchParams[key];
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          formatted[key] = value.join(',');
        } else {
          formatted[key] = value.toString();
        }
      }
    });

    return formatted;
  },

  // Get Member Status Badge
  getMemberStatusBadge(member) {
    if (!member.is_active) {
      return { text: 'Inactive', class: 'inactive', color: '#e53e3e' };
    }

    switch (member.status) {
      case 'approved':
        return { text: 'Approved', class: 'approved', color: '#38a169' };
      case 'pending':
        return { text: 'Pending', class: 'pending', color: '#d69e2e' };
      case 'rejected':
        return { text: 'Rejected', class: 'rejected', color: '#e53e3e' };
      case 'active':
        return { text: 'Active', class: 'active', color: '#38a169' };
      case 'suspended':
        return { text: 'Suspended', class: 'suspended', color: '#d69e2e' };
      case 'expired':
        return { text: 'Expired', class: 'expired', color: '#e53e3e' };
      default:
        return { text: 'Unknown', class: 'unknown', color: '#718096' };
    }
  },


  // Generate Member ID
  generateMemberId() {
    const year = new Date().getFullYear();
    const random = Math.random().toString(36).substr(2, 6).toUpperCase();
    return `ASF${year}${random}`;
  },

  // Format Date for Display
  formatDate(dateString) {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  },

  // Format DateTime for Display
  formatDateTime(dateString) {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

export { memberService };
export default memberService;